/**
 * Cookiebot Service
 * 
 * Handles Cookiebot consent management for GTM-loaded Cookiebot.
 * Designed for Next.js with SSR safety and performance optimization.
 * 
 * This service provides:
 * - Consent retrieval from window.Cookiebot (loaded via GTM)
 * - Consent event handling (accept/decline)
 * - LocalStorage persistence for caching
 * - Type-safe consent access
 * 
 * Note: Cookiebot is loaded via Google Tag Manager, not manually initialized.
 */

/**
 * Check if Cookiebot is available in the global scope
 */
const isCookiebotAvailable = () => {
  return typeof window !== 'undefined' && window.Cookiebot;
};

/**
 * Get the current Cookiebot consent object
 * @returns {Object|null} Consent object or null if not available
 */
export const getCookiebotConsent = () => {
  if (!isCookiebotAvailable()) return null;
  
  try {
    const consent = window.Cookiebot?.consent || {};
    return {
      necessary: consent.necessary || false,
      preferences: consent.preferences || false,
      statistics: consent.statistics || false,
      marketing: consent.marketing || false,
      method: consent.method || null, // How consent was given
      stamp: consent.stamp || null, // Timestamp
    };
  } catch (error) {
    console.error('Error getting Cookiebot consent:', error);
    return null;
  }
};

/**
 * Get consent from localStorage (fallback/cached version)
 * @returns {Object|null} Cached consent object or null
 */
export const getCachedConsent = () => {
  if (typeof window === 'undefined') return null;
  
  try {
    const cached = localStorage.getItem('cookie-consent');
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.error('Error reading cached consent:', error);
    return null;
  }
};

/**
 * Store consent in localStorage for persistence and API usage
 * @param {Object} consent - The consent object to store
 */
export const storeConsent = (consent) => {
  if (typeof window === 'undefined' || !consent) return;
  
  try {
    localStorage.setItem('cookie-consent', JSON.stringify(consent));
  } catch (error) {
    console.error('Error storing consent:', error);
  }
};

/**
 * Subscribe to Cookiebot consent accept event
 * Stores consent in localStorage when user accepts
 * 
 * @param {Function} callback - Optional callback to execute on accept
 */
export const onConsentAccept = (callback) => {
  if (!isCookiebotAvailable()) return;

  try {
    window.addEventListener('CookiebotOnAccept', () => {
      const consent = getCookiebotConsent();
      if (consent) {
        storeConsent(consent);
        if (callback) callback(consent);
      }
    }, false);
  } catch (error) {
    console.error('Error subscribing to consent accept:', error);
  }
};

/**
 * Subscribe to Cookiebot consent decline event
 * Stores consent (all false) in localStorage when user declines
 * 
 * @param {Function} callback - Optional callback to execute on decline
 */
export const onConsentDecline = (callback) => {
  if (!isCookiebotAvailable()) return;

  try {
    window.addEventListener('CookiebotOnDecline', () => {
      const consent = getCookiebotConsent();
      if (consent) {
        storeConsent(consent);
        if (callback) callback(consent);
      }
    }, false);
  } catch (error) {
    console.error('Error subscribing to consent decline:', error);
  }
};